class CleoVolumeDiscount extends HTMLElement {
  connectedCallback() {
    const raw = this.querySelector('[data-product-json]');
    if (!raw) return;

    this.productData = JSON.parse(raw.textContent);
    this.discount = this.dataset.discount || '';
    this.selectedPack = 2;
    this.ctaOriginalHTML = '';
    this._isFillingDropdowns = false;

    this._bindRadios();
    this._bindSelects();
    this._bindCta();
    this._applyPackState(2);
    this._syncReleasit();
  }

  _bindRadios() {
    this.querySelectorAll('.cvd__radio').forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.checked) this._applyPackState(parseInt(radio.value, 10));
      });
    });
  }

  _bindSelects() {
    this.querySelectorAll('.cvd__select').forEach((select) => {
      select.addEventListener('change', () => this._validate());
    });
  }

  _bindCta() {
    const cta = this.querySelector('[data-cta]');
    if (!cta) return;
    this.ctaOriginalHTML = cta.innerHTML;

    // Capture phase: el botón NO tiene [disabled] nativo, así que los eventos llegan.
    // Chequeamos la clase para decidir si bloquear o proceder.
    cta.addEventListener(
      'click',
      (e) => {
        if (cta.classList.contains('cvd-btn-disabled')) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this._highlightMissingVariants();
          return;
        }
        this._handleCheckout();
      },
      { capture: true }
    );
  }

  _applyPackState(packNum) {
    this.selectedPack = packNum;

    this.querySelectorAll('[data-card]').forEach((card) => {
      const isActive = parseInt(card.dataset.card, 10) === packNum;
      card.classList.toggle('cvd__card--active', isActive);
    });

    this.querySelectorAll('[data-pack-units]').forEach((panel) => {
      const isActive = parseInt(panel.dataset.packUnits, 10) === packNum;
      panel.classList.toggle('cvd__units--open', isActive);
    });

    this._updateCtaPrice();
    this._validate();
  }

  _updateCtaPrice() {
    const priceEl = this.querySelector('[data-cta-price]');
    if (!priceEl) return;

    const activeCard = this.querySelector(`[data-card="${this.selectedPack}"]`);
    const payQty = activeCard ? parseInt(activeCard.dataset.payQty, 10) : 1;
    const unitPriceCents = parseInt(this.dataset.unitPrice, 10) || 0;
    if (!unitPriceCents) return;

    const packTotalCents = unitPriceCents * payQty;
    const onlinePrice = Math.round(packTotalCents * 0.9) / 100;

    priceEl.textContent = new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 0,
    }).format(onlinePrice);
  }

  _findVariant(optionValues) {
    return (
      this.productData.variants.find((v) => {
        if (v.options.length !== optionValues.length) return false;
        const _n = (x) => String(x).trim().toLowerCase().replace(/\s+/g, ""); return v.options.every((opt, i) => _n(opt) === _n(optionValues[i]));
      }) || null
    );
  }

  // Centraliza el estado enabled/disabled del CTA usando clase (no atributo)
  _setCtaDisabled(cta, isDisabled) {
    cta.classList.toggle('cvd-btn-disabled', isDisabled);
    // aria-disabled para accesibilidad
    cta.setAttribute('aria-disabled', String(isDisabled));
  }

  _validate() {
    const cta = this.querySelector('[data-cta]');
    if (!cta) return;

    if (this.productData.hasOnlyDefault) {
      this._setCtaDisabled(cta, false);
      this._updateProductForm();
      return;
    }

    const activePanel = this.querySelector(`[data-pack-units="${this.selectedPack}"]`);
    if (!activePanel) {
      this._setCtaDisabled(cta, true);
      return;
    }

    const units = activePanel.querySelectorAll('[data-unit]');
    let allValid = true;

    units.forEach((unit) => {
      const selects = unit.querySelectorAll('.cvd__select');
      const optionValues = Array.from(selects).map((s) => s.value);
      const incomplete = optionValues.some((v) => !v);

      let unavailableMsg = unit.querySelector('.cvd__unavailable');

      if (incomplete) {
        allValid = false;
        selects.forEach((s) => s.classList.remove('cvd__select--unavailable'));
        if (unavailableMsg) unavailableMsg.classList.remove('cvd__unavailable--visible');
        return;
      }

      const variant = this._findVariant(optionValues);
      const isAvailable = variant && variant.available;

      selects.forEach((s) => s.classList.toggle('cvd__select--unavailable', !isAvailable));

      if (!unavailableMsg) {
        unavailableMsg = document.createElement('p');
        unavailableMsg.className = 'cvd__unavailable';
        unavailableMsg.textContent = 'Combinación no disponible';
        unit.appendChild(unavailableMsg);
      }
      unavailableMsg.classList.toggle('cvd__unavailable--visible', !isAvailable);

      if (!isAvailable) allValid = false;
    });

    this._setCtaDisabled(cta, !allValid);
    if (allValid) this._updateProductForm();
  }

  // ── Sync variant → native product form (para que Releasit lo lea) ─────────
  _updateProductForm() {
    const activeCard = this.querySelector(`[data-card="${this.selectedPack}"]`);
    const getQty = activeCard ? parseInt(activeCard.dataset.getQty, 10) : 1;

    let firstVariantId = null;

    if (this.productData.hasOnlyDefault) {
      firstVariantId = this.productData.variants[0]?.id;
    } else {
      const activePanel = this.querySelector(`[data-pack-units="${this.selectedPack}"]`);
      if (activePanel) {
        const firstUnit = activePanel.querySelector('[data-unit]');
        if (firstUnit) {
          const vals = Array.from(firstUnit.querySelectorAll('.cvd__select')).map((s) => s.value);
          if (!vals.some((v) => !v)) {
            const variant = this._findVariant(vals);
            if (variant) firstVariantId = variant.id;
          }
        }
      }
    }

    if (!firstVariantId) return;

    const formInput = document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
    if (formInput && formInput.value !== String(firstVariantId)) {
      formInput.value = firstVariantId;
      formInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const qtyInput = document.querySelector('form[data-type="add-to-cart-form"] [name="quantity"]');
    if (qtyInput && qtyInput.value !== String(getQty)) {
      qtyInput.value = getQty;
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // ── Disabled state sync → Releasit button ─────────────────────────────────
  _syncReleasit() {
    const cta = this.querySelector('[data-cta]');
    if (!cta) return;

    const releasitBlock =
      document.querySelector('[id*="releasit_cod_form_button_app_block"]') ||
      document.querySelector('[id*="releasit"]') ||
      document.querySelector('[data-block-handle*="releasit"]');

    if (!releasitBlock) {
      console.warn('[CLEO Sync] Releasit block no encontrado en el DOM.');
    }

    const isCvdDisabled = () => cta.classList.contains('cvd-btn-disabled');

    const applyVisualState = (isDisabled) => {
      if (!releasitBlock) return;
      releasitBlock.classList.toggle('releasit-disabled', isDisabled);
      releasitBlock.querySelectorAll('button, input[type="submit"]').forEach((btn) => {
        btn.classList.toggle('releasit-disabled', isDisabled);
        if (isDisabled) {
          btn.setAttribute('data-cleo-disabled', '');
          btn.disabled = true;
        } else if (btn.hasAttribute('data-cleo-disabled')) {
          btn.removeAttribute('data-cleo-disabled');
          btn.disabled = false;
        }
      });
    };

    applyVisualState(isCvdDisabled());

    // Observar cambios de clase en el CTA (Estrategia B usa clase, no atributo)
    new MutationObserver(() => applyVisualState(isCvdDisabled())).observe(cta, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Click blocker en body (capture)
    document.body.addEventListener(
      'click',
      (e) => {
        if (!isCvdDisabled()) return;
        const isInsideReleasit =
          e.target.closest('[id*="releasit"]') ||
          e.target.closest('[data-block-handle*="releasit"]') ||
          (releasitBlock && releasitBlock.contains(e.target));
        if (!isInsideReleasit) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        this._highlightMissingVariants();
      },
      { capture: true }
    );

    this._hookReleasitClick(releasitBlock, isCvdDisabled);
  }

  // ── Hook al click de Releasit → esperar modal → sync ─────────────────────
  _hookReleasitClick(releasitBlock, isCvdDisabled) {
    if (!releasitBlock) return;

    releasitBlock.addEventListener('click', () => {
      if (isCvdDisabled()) return;
      this._preModifyReleasitGlobals();
      this._waitForReleasitModal(3000).then((modal) => {
        if (!modal) {
          console.warn('[CLEO Sync] Modal Releasit no apareció en 3s.');
          return;
        }
        // Fill rápido (banner + selects genérico)
        setTimeout(() => this._syncToReleasitModal(modal), 600);

        // Fill específico V1 con logs de diagnóstico + retry ante reset
        setTimeout(() => this._fillReleasitDropdowns(modal), 400);

        // Observar cambios de pack DENTRO del modal (por si Releasit
        // re-renderiza al cambiar de cantidad/pack interno)
        this._observeReleasitPackChange(modal);
      });
    });
  }

  // ── Fill dedicado a Releasit V1: selects nativos + logs + retry ────────────
  _fillReleasitDropdowns(modal) {
    if (this._isFillingDropdowns) return;
    this._isFillingDropdowns = true;

    if (this.productData.hasOnlyDefault) { this._isFillingDropdowns = false; return; }

    const cvdSelections = this._getCvdSelections();
    if (!cvdSelections.length) return;

    const optionCount = this.productData.options?.length || 1;
    const allSelects = Array.from(modal.querySelectorAll('select'));

    if (!allSelects.length) { this._isFillingDropdowns = false; return; }

    // ── Aplicar valores ────────────────────────────────────────────────────
    const apply = () => {
      cvdSelections.forEach((unitValues, unitIdx) => {
        unitValues.forEach(({ optionIndex, value }) => {
          const posIdx = unitIdx * optionCount + optionIndex;
          const sel = allSelects[posIdx];
          if (!sel) return;
          const matched = this._findMatchingOption(sel, value);
          if (!matched) return;
          if (sel.value !== matched) {
            sel.value = matched;
            sel.dispatchEvent(new Event('input',  { bubbles: true }));
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    };

    apply();

    // ── Retry: detectar si Releasit resetea los selects después del fill ──
    const snapshots = allSelects.map((s) => s.value);
    let retries = 0;
    const maxRetries = 6;

    const retryInterval = setInterval(() => {
      retries++;
      const wasReset = allSelects.some((s, i) => s.value !== snapshots[i]);

      if (wasReset) {
        apply();
        allSelects.forEach((s, i) => { snapshots[i] = s.value; });
      }

      if (retries >= maxRetries) {
        clearInterval(retryInterval);
      }
    }, 400);

    // Resetear el flag después de que el watchdog completo termine (6 × 400ms + margen)
    setTimeout(() => { this._isFillingDropdowns = false; }, 6 * 400 + 100);
  }

  // ── Observar cambios de pack dentro del modal (re-fill automático) ────────
  _observeReleasitPackChange(modal) {
    let debounce;
    new MutationObserver(() => {
      // Ignorar mutaciones causadas por nuestros propios dispatchEvent
      if (this._isFillingDropdowns) return;
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        this._fillReleasitDropdowns(modal);
      }, 300);
    }).observe(modal, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'aria-selected', 'data-active'] });
  }

  // ── Intentar pre-modificar globals de Releasit antes de que abra el modal ─
  _preModifyReleasitGlobals() {
    const cvdSelections = this._getCvdSelections();
    if (!cvdSelections.length) return;

    const offers = window._RSI_COD_FORM_QUANTIY_OFFERS;
    if (Array.isArray(offers) && offers.length) {
      const offer = offers[this.selectedPack - 1];
      if (offer) {
        const firstVariantId = this._resolveFirstVariantId();
        if (firstVariantId) {
          ['variantId', 'variant_id', 'selectedVariant', 'selected_variant_id'].forEach((prop) => {
            if (prop in offer) offer[prop] = firstVariantId;
          });
          if (Array.isArray(offer.lineItems)) {
            offer.lineItems.forEach((li, i) => {
              const sel = cvdSelections[i];
              if (!sel) return;
              const variant = this._findVariant(sel.map((s) => s.value));
              if (!variant) return;
              ['variantId', 'variant_id', 'selectedVariant'].forEach((prop) => {
                if (prop in li) li[prop] = variant.id;
              });
            });
          }
        }
      }
    }

    const rsi = window._rsi;
    if (rsi && typeof rsi === 'object') {
      const firstVariantId = this._resolveFirstVariantId();
      ['setVariant', 'setSelectedVariant', 'setLineItem', 'selectVariant', 'preSelectVariant'].forEach((method) => {
        if (typeof rsi[method] === 'function') {
          try { rsi[method](firstVariantId); } catch (e) { /* no-op */ }
        }
      });
    }
  }

  _resolveFirstVariantId() {
    if (this.productData.hasOnlyDefault) return this.productData.variants[0]?.id || null;
    const activePanel = this.querySelector(`[data-pack-units="${this.selectedPack}"]`);
    if (!activePanel) return null;
    const firstUnit = activePanel.querySelector('[data-unit]');
    if (!firstUnit) return null;
    const vals = Array.from(firstUnit.querySelectorAll('.cvd__select')).map((s) => s.value);
    if (vals.some((v) => !v)) return null;
    return this._findVariant(vals)?.id || null;
  }

  _waitForReleasitModal(timeout) {
    return new Promise((resolve) => {
      const start = Date.now();
      const findModal = () => {
        const candidates = [
          document.querySelector('[class*="releasit"][class*="modal"]:not([hidden])'),
          document.querySelector('[id*="releasit"][class*="modal"]:not([hidden])'),
          document.querySelector('[class*="releasit-cod"]:not([hidden])'),
          document.querySelector('[class*="releasit"]:not([hidden])'),
          ...Array.from(document.querySelectorAll('form')).filter(
            (f) => f.offsetParent !== null && f.querySelectorAll('select').length > 1
          ),
        ].filter(Boolean);

        if (candidates.length > 0) {
          resolve(candidates[0]);
        } else if (Date.now() - start < timeout) {
          setTimeout(findModal, 150);
        } else {
          resolve(null);
        }
      };
      setTimeout(findModal, 150);
    });
  }

  _findMatchingOption(select, targetValue) {
    const norm = String(targetValue).trim().toUpperCase();
    for (const opt of select.options) {
      if (!opt.value) continue;
      const v = String(opt.value).trim().toUpperCase();
      const t = String(opt.text).trim().toUpperCase();
      if (v === norm || t === norm) return opt.value;
      if (v.includes(norm) || norm.includes(v)) return opt.value;
      if (t.includes(norm) || norm.includes(t)) return opt.value;
      const parts = norm.split(/[\s/,]+/).filter(Boolean);
      if (parts.length > 1 && parts.every((p) => v.includes(p) || t.includes(p))) return opt.value;
    }
    return null;
  }

  _getCvdSelections() {
    const activePanel = this.querySelector(`[data-pack-units="${this.selectedPack}"]`);
    if (!activePanel) return [];
    const result = [];
    activePanel.querySelectorAll('[data-unit]').forEach((unit) => {
      const row = [];
      unit.querySelectorAll('.cvd__select').forEach((s) => {
        row.push({ optionIndex: parseInt(s.dataset.optionIndex, 10), value: s.value });
      });
      result.push(row);
    });
    return result;
  }

  _syncToReleasitModal(modal) {
    if (this.productData.hasOnlyDefault) return;

    const cvdSelections = this._getCvdSelections();
    const optionNames = this.productData.options || [];
    const optionCount = optionNames.length || 1;

    const modalSelects = modal.querySelectorAll('select');
    if (modalSelects.length > 0) {
      cvdSelections.forEach((unitValues, unitIdx) => {
        unitValues.forEach(({ optionIndex, value }) => {
          const posIdx = unitIdx * optionCount + optionIndex;
          let sel = modalSelects[posIdx] || null;
          if (!sel) {
            const oName = optionNames[optionIndex] || '';
            sel = oName ? modal.querySelector(`select[name*="${oName}"]`) : null;
          }
          if (!sel) return;
          const matched = this._findMatchingOption(sel, value);
          if (matched) {
            sel.value = matched;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }

    const VARIANT_RE = /^[\s]*(S|M|L|XL|2XL|XS|XXL|NEGRO|BLANCO|CAFE|GRIS|VERDE|BEIGE|AZUL|ROJO|\d+-\d+|\d+)[\s]*$/i;
    const retryDropdowns = Array.from(modal.querySelectorAll('*')).filter((el) => {
      const tag = el.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE') return false;
      const text = el.innerText || el.textContent || '';
      if (!VARIANT_RE.test(text.trim()) || el.children.length > 3) return false;
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });

    if (retryDropdowns.length > 0) {
      this._clickSyncDropdowns(modal, retryDropdowns, cvdSelections);
      return;
    }

    this._showReleasitBanner(modal, cvdSelections);
  }

  _clickSyncDropdowns(modal, dropdowns, cvdSelections) {
    const optionCount = this.productData.options?.length || 1;

    const syncNext = (idx) => {
      if (idx >= dropdowns.length) return;
      const unitIdx = Math.floor(idx / optionCount);
      const optIdx = idx % optionCount;
      const targetValue = cvdSelections[unitIdx]?.[optIdx]?.value;
      if (!targetValue) { syncNext(idx + 1); return; }

      dropdowns[idx].click();

      setTimeout(() => {
        const NORM = targetValue.trim().toUpperCase();
        const optionEl = Array.from(
          document.querySelectorAll('[class*="option"],[class*="item"],[role="option"],li')
        ).find((el) => {
          if (!el.offsetParent) return false;
          const t = (el.innerText || el.textContent || '').trim().toUpperCase();
          return t === NORM || t.includes(NORM) || NORM.includes(t);
        });

        if (optionEl) {
          optionEl.click();
        } else {
          document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
        }
        setTimeout(() => syncNext(idx + 1), 250);
      }, 200);
    };

    syncNext(0);
  }

  _showReleasitBanner(modal, cvdSelections) {
    if (modal.querySelector('.cleo-releasit-banner')) return;

    const lines = cvdSelections
      .map((unitVals, i) => {
        const vals = unitVals.map((v) => v.value).filter(Boolean).join(' / ');
        return vals ? `<li>Prenda #${i + 1}: <strong>${vals}</strong></li>` : '';
      })
      .join('');

    if (!lines) return;

    const banner = document.createElement('div');
    banner.className = 'cleo-releasit-banner';
    banner.innerHTML =
      `<p>Elegiste estas variantes en el selector 1:</p><ul>${lines}</ul>` +
      `<p>Seleccioná los mismos valores aquí abajo antes de confirmar.</p>`;
    banner.style.cssText = [
      'background:#fffbe6', 'border-left:4px solid #f59e0b',
      'border-radius:8px', 'padding:12px 16px', 'margin-bottom:16px',
      'font-size:0.875rem', 'line-height:1.5', 'color:#1f2937',
    ].join(';');
    banner.querySelector('ul').style.cssText = 'margin:6px 0 6px 16px;padding:0';
    banner.querySelector('p:last-child').style.cssText = 'margin:6px 0 0;opacity:0.7';

    modal.insertBefore(banner, modal.firstChild);
  }

  // ── Feedback visual en selects vacíos cuando el usuario intenta comprar ──
  _highlightMissingVariants() {
    const activePanel = this.querySelector(`[data-pack-units="${this.selectedPack}"]`);
    if (!activePanel) return;

    const emptySelects = Array.from(activePanel.querySelectorAll('.cvd__select')).filter(
      (s) => !s.value
    );
    if (!emptySelects.length) return;

    emptySelects.forEach((s, i) => {
      s.classList.remove('cvd-required-error');
      void s.offsetWidth; // forzar reflow para reiniciar animación
      s.classList.add('cvd-required-error');

      const clearError = () => s.classList.remove('cvd-required-error');
      s.addEventListener('focus', clearError, { once: true });
      s.addEventListener('change', clearError, { once: true });

      if (i === 0) {
        setTimeout(() => {
          const rect = s.getBoundingClientRect();
          const offset = 80;
          if (rect.top < offset || rect.bottom > window.innerHeight) {
            window.scrollBy({ top: rect.top - offset, behavior: 'smooth' });
          }
        }, 0);
      }
    });
  }

  _buildItems() {
    const activeCard = this.querySelector(`[data-card="${this.selectedPack}"]`);
    const getQty = activeCard ? parseInt(activeCard.dataset.getQty, 10) : 1;

    if (this.productData.hasOnlyDefault) {
      const defaultId = this.productData.variants[0]?.id;
      if (!defaultId) return null;
      return Array.from({ length: getQty }, () => ({ id: defaultId, quantity: 1 }));
    }

    const activePanel = this.querySelector(`[data-pack-units="${this.selectedPack}"]`);
    if (!activePanel) return null;

    const units = activePanel.querySelectorAll('[data-unit]');
    const items = [];

    for (const unit of units) {
      const selects = unit.querySelectorAll('.cvd__select');
      const optionValues = Array.from(selects).map((s) => s.value);

      if (optionValues.some((v) => !v)) return null;

      const variant = this._findVariant(optionValues);
      if (!variant || !variant.available) return null;

      items.push({ id: variant.id, quantity: 1 });
    }

    return items.length > 0 ? items : null;
  }

  async _handleCheckout() {
    const items = this._buildItems();
    if (!items) return;

    const cta = this.querySelector('[data-cta]');
    const errorEl = this.querySelector('[data-error]');

    // Durante el loading usamos disabled nativo + clase para bloquear todo
    this._setCtaDisabled(cta, true);
    cta.disabled = true;
    cta.classList.add('cvd__cta--loading');
    cta.textContent = 'Procesando...';
    if (errorEl) errorEl.hidden = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || 'Error al agregar al carrito. Intenta de nuevo.');
      }

      const checkoutUrl = this.discount
        ? `/checkout?discount=${encodeURIComponent(this.discount)}`
        : '/checkout';

      window.location.href = checkoutUrl;
    } catch (error) {
      cta.innerHTML = this.ctaOriginalHTML;
      cta.classList.remove('cvd__cta--loading');
      cta.disabled = false;
      this._setCtaDisabled(cta, false);
      this._updateCtaPrice();

      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      }
    }
  }
}

customElements.define('cleo-volume-discount', CleoVolumeDiscount);

// ── Mover Releasit block dentro del cvd__cta-card ─────────────────────────────
function _moveReleasitIntoCard() {
  const releasitBlock = document.querySelector('[id^="shopify-block"][id*="releasit"]');
  const cvdCard = document.querySelector('.cvd__cta-card');
  const shippingLine = cvdCard?.querySelector('.cvd__shipping-line');

  if (!releasitBlock || !cvdCard || !shippingLine) return;
  if (cvdCard.contains(releasitBlock)) return;

  cvdCard.insertBefore(releasitBlock, shippingLine);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _moveReleasitIntoCard);
} else {
  _moveReleasitIntoCard();
}

const _releasitMoveObserver = new MutationObserver((mutations) => {
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (node.nodeType !== 1) continue;
      const id = String(node.id || '').toLowerCase();
      if (id.includes('releasit') || node.querySelector?.('[id*="releasit"]')) {
        _moveReleasitIntoCard();
        return;
      }
    }
  }
});
_releasitMoveObserver.observe(document.body, { childList: true, subtree: true });
